import React, { useCallback, useEffect, useState } from 'react'
import { Col, ModalBody, ModalFooter, Row } from 'reactstrap'
import { BasicHeader } from '@/dialogs/common/basicHeader'
import Resx from '@/components/Resx'
import { usePermissionChecker } from '@/hooks/usePermissionChecker'
import { actionCreators } from '@/store/dataCrossing'
import { useDispatch } from '@/hooks/dispatch'
import { BoxHeader, WhiteBox } from '@/components/Layout'
import { getName } from '@/utils/nameHelpers'
import IndividualDetail from '@/internal-app/pages/dataCrossing/individual-detail'
import { useLocalization } from '@/hooks/localization'
import { ValidationBoundary } from '@/components/validation'
import {
  dataCrossingProblematicCombinationStatus
} from '@/internal-app/pages/dataCrossing/dataCrossingProblematicCombinationStatus'
import getEnumTranslation from '@/enums/enums'
import { ProblematicCombinationState } from '@/enums/problematicCombinationState'
import {
  BooleanColumn,
  BsrsGrid,
  DateCell,
  DateFilterCell,
  LookupCell,
  LookupNameColumn,
  NameColumn
} from '@/components/BsrsGrid'
import context from '@/context'
import { GridColumn as Column } from '@progress/kendo-react-grid/dist/npm/GridColumn'
import { openErrorDialog } from '@/utils/dialogHelpers'
import LoadingIndicator from '@/components/LoadingIndicator'

const ProblematicCombinationDialog = React.memo(function ProblematicCombinationDialog (props) {
  const { combination, closeDialog, individuals } = props
  const isGranted = usePermissionChecker()
  const { localize, locale } = useLocalization()
  const [setProblematicCombination] = useDispatch([actionCreators.setProblematicCombination])
  const [user, setUser] = useState(null)

  // Loading user effect
  useEffect(() => {
    if (combination.type === 0) {
      context.api.userManagementService.getUserByIndividual(individuals[0].id)
        .then(res => setUser(res))
        .catch(e => openErrorDialog({ customErrorText: e }))
    }
  }, [combination.type]) // eslint-disable-line

  const onClick = useCallback((ok) => {
    setProblematicCombination(combination.id, ok)
    closeDialog()
  }, [closeDialog, combination.id, setProblematicCombination])

  function renderIndividualInfo (individual) {
    return <WhiteBox key={individual.id}>
      <div className='comparison-header'>
        <BoxHeader className='header'>{getName(individual)}</BoxHeader>
      </div>
      <IndividualDetail individual={individual} localize={localize} onNavigate={closeDialog} />
    </WhiteBox>
  }

  function renderJobs () {
    if (isGranted('CanViewOrganizationalUnitManagement')) {
      return (
        user === null
          ? <LoadingIndicator />
          : <WhiteBox header='JobPosition.Jobs'>
            <BsrsGrid
              simpleView
              dataSource={req => context.api.jobsService.getUserJobsTableData(user.id, req)}>
              {NameColumn({
                locale,
                nameProp: 'organizationalUnitName',
                latinNameProp: 'organizationalUnitLatinName',
                title: localize('OrganizationalUnit.Title'),
                editable: false
              })}
              {NameColumn({
                locale,
                nameProp: 'jobPositionName',
                latinNameProp: 'jobPositionLatinName',
                title: localize('JobPosition.JobPositionTitle'),
                editable: false
              })}
              <Column field='validFrom' title={localize('Common.ValidFrom')} cell={DateCell}
                filterCell={DateFilterCell} filter='date' width='150px' />
              <Column field='validTo' title={localize('Common.ValidTo')} cell={DateCell} filterCell={DateFilterCell}
                filter='date' width='150px' />
              {BooleanColumn({ field: 'acting', title: localize('Common.Acting'), width: '100px', editable: false })}
            </BsrsGrid>
          </WhiteBox>

      )
    }
  }

  function renderAccessedInstitutions () {
    return user === null
      ? <LoadingIndicator />
      : <WhiteBox header='UsersManagement.ReportAppAccess'>
        <BsrsGrid items={user.allowedInstitutions} simpleView>
          <Column field='name' title={localize('Common.Name')} />
          <Column field='latinName' title={localize('Common.LatinName')} />
        </BsrsGrid>
      </WhiteBox>
  }

  function renderRelatedPersons (individual) {
    return <WhiteBox header='Individuals.RelatedInstitutions'>
      <BsrsGrid
        dataSource={req => context.api.individualService.getRelatedPersons(individual.id, req)}
        simpleView>
        <Column field='institution' title={localize('Individuals.Institution')} cell={LookupCell} width='200px' />
        {LookupNameColumn({ locale, field: 'relatedPersonType', title: localize('Individuals.RelatedPersonType'), editable: false, width: '100px' })}
        <Column field='validFrom' title={localize('Common.ValidFrom')} cell={DateCell} filterCell={DateFilterCell} filter='date' width='100px' />
        <Column field='validTo' title={localize('Common.ValidTo')} cell={DateCell} filterCell={DateFilterCell} filter='date' width='100px' />
      </BsrsGrid>
    </WhiteBox>
  }

  function renderInfo () {
    switch (combination.type) {
      case 0:
        const individual = individuals[0]
        return <Row>
          <Col>{renderIndividualInfo(individual)}</Col>
          <Col>
            {renderJobs()}
            {renderAccessedInstitutions()}
            {renderRelatedPersons(individual)}
          </Col>
        </Row>
      case 1:
        return <React.Fragment>
          <Row>
            {individuals.map(x => <Col lg='6'>
              {renderIndividualInfo(x)}
            </Col>)}
          </Row>
          <Row>
            {individuals.map(x => <Col lg='6'>{renderRelatedPersons(x)}</Col>)}
          </Row>
        </React.Fragment>
    }
  }

  const status = getEnumTranslation({ localize, enumName: ProblematicCombinationState.enumName, value: combination.state })
  return <ValidationBoundary>
    <BasicHeader title='DataCrossings.ProblematicCombination' {...props} />
    <ModalBody>
      <Row>
        <Col>
          <h4>
            <span className={'status status--' + dataCrossingProblematicCombinationStatus(combination.state) + ' inline'}>{status}</span>
            <Resx name={'DataCrossings.ProblematicCombinationExplain_' + combination.type} />
          </h4>
        </Col>
      </Row>
      {renderInfo()}
    </ModalBody>
    <ModalFooter>
      {isGranted('CanManageDataCrossingsDelinquents') && combination.state !== ProblematicCombinationState.enum.Resolved && <React.Fragment>
        <button className='btn btn-md btn-primary' onClick={() => onClick(true)}><Resx name='DataCrossings.CombinationOK' /></button>
        <button className='btn btn-md btn-primary btn-danger' onClick={() => onClick(false)}><Resx name='DataCrossings.ProblematicCombination' /></button>
      </React.Fragment>}
    </ModalFooter>
  </ValidationBoundary>
})

export default {
  modalProps: {
    size: 'lg'
  },
  component: ProblematicCombinationDialog
}



// WEBPACK FOOTER //
// ./src/dialogs/problematicCombination.js